// â”€â”€â”€ SDK: userless access (Phase 6) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Login flows for users WITHOUT a `users/{uid}` doc â€” the ~280 magic-link
// employees. Uses Firebase Auth as the session layer (creates a Firebase
// Auth identity, but we never write `users/`). After Firebase auth completes,
// look up `contacts where phone == fbUser.phoneNumber` (or email) to get
// the contactId for scope.
//
// Kill switch: contact.status must be 'active'. If inactive, signOut + error.
//
// Phase 11 will add a JWT custom session for shared SSO across sibling apps.
// For Phase 6 / MVP, Firebase Auth's per-origin session is enough.

import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
  signOut,
} from 'firebase/auth';
import { auth } from '../../config/firebase';
import { findContactByEmail, findContactByPhone } from '../../services/contactsService';
import { normalizePhone, isValidPhone } from '../../lib/phone';
import { isValidEmail, normalizeEmail } from '../../lib/email';
import type { Contact } from '../../types';

// â”€â”€â”€ Phone (SMS OTP via Firebase Phone Auth) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

let recaptchaVerifier: RecaptchaVerifier | null = null;

/** Initialize the invisible reCAPTCHA verifier. Call once per page, attach to
 *  a DOM element id (typically a hidden div). */
export function initRecaptcha(containerId: string): RecaptchaVerifier {
  if (recaptchaVerifier) return recaptchaVerifier;
  recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => { /* solved, will proceed automatically */ },
  });
  return recaptchaVerifier;
}

/** Send SMS OTP to the given phone via Firebase Phone Auth. Returns a
 *  `ConfirmationResult` whose `.confirm(code)` resolves the login. */
export async function requestPhoneOtp(
  phoneInput: string,
  recaptchaContainerId: string,
): Promise<ConfirmationResult> {
  if (!isValidPhone(phoneInput)) {
    throw new Error(`Invalid Indonesian phone: ${phoneInput}`);
  }
  const e164 = normalizePhone(phoneInput);
  if (!e164) throw new Error(`Could not normalize phone: ${phoneInput}`);

  // Firebase expects E.164 with `+` prefix
  const fbPhone = `+${e164}`;

  // Look up contact FIRST to enforce kill switch BEFORE wasting an SMS
  const contact = await findContactByPhone(e164);
  if (!contact) {
    throw new Error('No contact registered with that phone. Hubungi HR.');
  }
  if (contact.status !== 'active') {
    throw new Error('Account inactive. Hubungi HR.');
  }

  const verifier = initRecaptcha(recaptchaContainerId);
  return signInWithPhoneNumber(auth, fbPhone, verifier);
}

/** After user enters OTP code, call this to confirm. Returns the contact
 *  associated with the verified phone. */
export async function verifyPhoneOtp(
  confirmation: ConfirmationResult,
  code: string,
): Promise<Contact> {
  const cred = await confirmation.confirm(code);
  const phone = cred.user.phoneNumber;
  if (!phone) throw new Error('Phone not present on credential after verify.');

  const e164 = phone.replace(/^\+/, '');
  const contact = await findContactByPhone(e164);
  if (!contact) {
    await signOut(auth);
    throw new Error('Contact disappeared mid-flow. Hubungi HR.');
  }
  if (contact.status !== 'active') {
    await signOut(auth);
    throw new Error('Account inactive. Hubungi HR.');
  }
  return contact;
}

// â”€â”€â”€ Email (magic link via Firebase Email Link) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const EMAIL_LINK_REDIRECT_KEY = 'rascalEmailLinkRedirect';
const EMAIL_LINK_EMAIL_KEY    = 'rascalEmailLinkEmail';

/** Send a sign-in link to the given email. The link returns to `redirectUrl`
 *  (typically the same page). Caller is expected to handle email re-prompt
 *  if the user opens the link in a different browser/device. */
export async function requestEmailLink(
  emailInput: string,
  redirectUrl: string,
): Promise<void> {
  if (!isValidEmail(emailInput)) {
    throw new Error(`Invalid email: ${emailInput}`);
  }
  const email = normalizeEmail(emailInput);

  // Kill switch BEFORE sending email
  const contact = await findContactByEmail(email);
  if (!contact) {
    throw new Error('No contact registered with that email. Hubungi HR.');
  }
  if (contact.status !== 'active') {
    throw new Error('Account inactive. Hubungi HR.');
  }

  await sendSignInLinkToEmail(auth, email, {
    url: redirectUrl,
    handleCodeInApp: true,
  });

  // Persist email for the verify step
  window.localStorage.setItem(EMAIL_LINK_EMAIL_KEY, email);
  window.localStorage.setItem(EMAIL_LINK_REDIRECT_KEY, redirectUrl);
}

/** Check if the current URL is a sign-in link. Call this on page load. */
export function isEmailLink(url: string): boolean {
  return isSignInWithEmailLink(auth, url);
}

/** Complete email-link sign-in. Falls back to prompting for email if it
 *  wasn't persisted in localStorage (different browser/device). */
export async function verifyEmailLink(currentUrl: string): Promise<Contact> {
  let email = window.localStorage.getItem(EMAIL_LINK_EMAIL_KEY) ?? '';
  if (!email) {
    email = window.prompt('Konfirmasi email kamu untuk lanjut sign-in:') ?? '';
  }
  if (!email) throw new Error('Email required to complete sign-in.');

  const cred = await signInWithEmailLink(auth, normalizeEmail(email), currentUrl);
  const userEmail = (cred.user.email ?? email).toLowerCase();

  const contact = await findContactByEmail(userEmail);
  if (!contact) {
    await signOut(auth);
    throw new Error('Contact not found after sign-in. Hubungi HR.');
  }
  if (contact.status !== 'active') {
    await signOut(auth);
    throw new Error('Account inactive. Hubungi HR.');
  }

  window.localStorage.removeItem(EMAIL_LINK_EMAIL_KEY);
  window.localStorage.removeItem(EMAIL_LINK_REDIRECT_KEY);
  return contact;
}

// â”€â”€â”€ Logout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export async function userlessSignOut(): Promise<void> {
  await signOut(auth);
}
